import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import icon from "../../assets/icons/search1.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./SearchInput.css";
import {
  MIN_CHARS,
  SUGGESTION_DEBOUNCE_MS,
} from "../../utils/searchUtils";
import {
  loadSearchVocabulary,
  buildPinnedSuggestionList,
} from "../../utils/searchVocabulary";
import {
  RESTRICTED_SEARCH_MESSAGE,
  containsRestrictedKeyword,
  filterSafeSearchValues,
} from "../../utils/restrictedKeywords";
import { useTheme } from "../../context/ThemeProvider";

const SUGGESTION_CACHE_KEY = "shotkut-search-suggestions-v1";

/** Keep ?mode=ai|vector when submitting a new term (reads current URL only). */
function preserveSearchModeInParams(params) {
  const mode = String(
    new URLSearchParams(window.location.search).get("mode") || "",
  )
    .trim()
    .toLowerCase();
  if (mode === "vector" || mode === "semantic") {
    params.set("mode", "vector");
  } else if (mode === "ai") {
    params.set("mode", "ai");
  }
}
const SUGGESTION_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const suggestionMemoryCache = new Map();

const getSuggestionCacheKey = (query) => String(query || "").trim().toLowerCase();

const readSuggestionCache = (query) => {
  const key = getSuggestionCacheKey(query);
  if (!key) return [];

  const memoryEntry = suggestionMemoryCache.get(key);
  if (memoryEntry && Date.now() - memoryEntry.savedAt < SUGGESTION_CACHE_TTL_MS) {
    return memoryEntry.suggestions || [];
  }

  try {
    const cache = JSON.parse(localStorage.getItem(SUGGESTION_CACHE_KEY) || "{}");
    const entry = cache[key];
    if (entry && Date.now() - entry.savedAt < SUGGESTION_CACHE_TTL_MS) {
      suggestionMemoryCache.set(key, entry);
      return entry.suggestions || [];
    }
  } catch (error) { }

  return [];
};

const writeSuggestionCache = (query, suggestions) => {
  const key = getSuggestionCacheKey(query);
  const cleaned = filterSafeSearchValues(
    (suggestions || []).map((value) => String(value || "").trim()).filter(Boolean),
  );
  if (!key || cleaned.length === 0) return;

  const entry = {
    savedAt: Date.now(),
    suggestions: cleaned.slice(0, 8),
  };
  suggestionMemoryCache.set(key, entry);

  try {
    const cache = JSON.parse(localStorage.getItem(SUGGESTION_CACHE_KEY) || "{}");
    cache[key] = entry;
    const trimmedCache = Object.fromEntries(
      Object.entries(cache)
        .filter(([, value]) => Date.now() - value.savedAt < SUGGESTION_CACHE_TTL_MS)
        .slice(-80)
    );
    localStorage.setItem(SUGGESTION_CACHE_KEY, JSON.stringify(trimmedCache));
  } catch (error) { }
};

const readPrefixSuggestionCache = (query) => {
  const normalizedQuery = getSuggestionCacheKey(query);
  if (!normalizedQuery) return [];

  const fromExact = readSuggestionCache(normalizedQuery);
  if (fromExact.length > 0) return fromExact;

  const prefixSuggestions = [];
  const addMatches = (suggestions) => {
    suggestions.forEach((suggestion) => {
      const value = String(suggestion || "").trim();
      if (
        !containsRestrictedKeyword(value) &&
        value.toLowerCase().includes(normalizedQuery)
      ) {
        prefixSuggestions.push(value);
      }
    });
  };

  suggestionMemoryCache.forEach((entry) => addMatches(entry.suggestions || []));

  try {
    const cache = JSON.parse(localStorage.getItem(SUGGESTION_CACHE_KEY) || "{}");
    Object.values(cache).forEach((entry) => {
      if (Date.now() - entry.savedAt < SUGGESTION_CACHE_TTL_MS) {
        addMatches(entry.suggestions || []);
      }
    });
  } catch (error) { }

  return prefixSuggestions;
};

const SearchInput = ({
  searchQuery,
  setSearchQuery,
  variant = "default",
  onSuggestionsChange,
  onDropdownOpenChange,
  className = "",
  inputClassName = "",
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // When set, suggestions[0] is the fuzzy correction (Did you mean label).
  const [didYouMean, setDidYouMean] = useState(null);
  const [, setSearchState] = useState("IDLE");
  const [, setLatestSuggestions] = useState([]);
  const isPageVariant = variant === "page";
  const isHeaderVariant = variant === "header";

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const skipSuggestionFetchRef = useRef(false);
  const suppressSuggestionsRef = useRef(false);

  const applyPinnedSuggestions = async (typed, apiSuggestions) => {
    const pinned = await buildPinnedSuggestionList(typed, apiSuggestions, 5);
    setSuggestions(pinned.suggestions);
    setLatestSuggestions(pinned.suggestions);
    setDidYouMean(pinned.didYouMean);
    onSuggestionsChange?.(pinned.suggestions);
    setIsDropdownOpen(pinned.suggestions.length > 0);
    if (pinned.suggestions.length > 0) {
      setSearchState("SHOW_SUGGESTIONS");
    }
    return pinned;
  };

  useEffect(() => {
    loadSearchVocabulary().catch(() => {});
  }, []);

  useEffect(() => {
    const urlTerm = (urlSearchParams.get("term") || "").trim();
    const currentInput = inputRef.current?.value || "";

    if (currentInput === urlTerm) {
      return;
    }

    skipSuggestionFetchRef.current = true;
    suppressSuggestionsRef.current = true;
    setInput(urlTerm);
    setSuggestions([]);
    setLatestSuggestions([]);
    setDidYouMean(null);
    setIsDropdownOpen(false);
    setActiveIndex(-1);
    onSuggestionsChange?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof onDropdownOpenChange === "function") {
      onDropdownOpenChange(isDropdownOpen);
    }
  }, [isDropdownOpen, onDropdownOpenChange]);

  useEffect(() => {
    if (!suggestions.length) {
      setIsDropdownOpen(false);
    }
  }, [suggestions]);

  const handleSearchClick = () => {
    performSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown" && suggestions.length > 0) {
      e.preventDefault();
      setIsDropdownOpen(true);
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === "ArrowUp" && suggestions.length > 0) {
      e.preventDefault();
      setIsDropdownOpen(true);
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
      return;
    }

    if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (e.key === "Enter") {
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        handleSuggestionClick(suggestions[activeIndex]);
        return;
      }
      performSearch();
    }
  };

  const performSearch = async () => {
    const trimmed = input.trim();
    console.log("Search function was called in React!", trimmed);
    if (!trimmed) {
      console.log("Input is empty, not navigating.");
      setSearchState("IDLE");
      return;
    }

    if (containsRestrictedKeyword(trimmed)) {
      toast.error(RESTRICTED_SEARCH_MESSAGE);
      setSuggestions([]);
      setLatestSuggestions([]);
      setDidYouMean(null);
      setIsDropdownOpen(false);
      onSuggestionsChange?.([]);
      setSearchState("IDLE");
      return;
    }

    if (trimmed.length < MIN_CHARS) {
      setSearchState(
        trimmed.length >= MIN_CHARS ? "SHOW_SUGGESTIONS" : "TYPING_PARTIAL"
      );
      setIsDropdownOpen(suggestions.length > 0);
      return;
    }

    // Prefer pinned #1 (fuzzy or exact typed) — same commit term as the dropdown.
    const pinned = await buildPinnedSuggestionList(trimmed, suggestions, 5);
    const commitTerm = pinned.commitTerm || pinned.prepared?.corrected;
    if (!commitTerm || pinned.prepared?.onlyStopwords) {
      toast.error(
        "Add a subject to search (e.g. car, beach). Video and footage aren't searchable alone.",
      );
      setSearchState("IDLE");
      return;
    }

    if (pinned.didYouMean) {
      toast.info(`Showing results for "${commitTerm}"`);
      setInput(commitTerm);
    }

    suppressSuggestionsRef.current = true;
    requestIdRef.current += 1;
    try {
      sessionStorage.setItem("shotkut-search-source", "typed");
    } catch (error) {
      // Ignore storage failures.
    }
    const params = new URLSearchParams();
    params.set("term", commitTerm);
    preserveSearchModeInParams(params);
    navigate(`/search?${params.toString()}`);
    setIsDropdownOpen(false);
    setSuggestions([]);
    setLatestSuggestions([]);
    setDidYouMean(null);
    onSuggestionsChange?.([]);
    setActiveIndex(-1);
    setSearchState("SEARCHING");
    try {
      inputRef.current?.blur();
    } catch (e) { }
  };

  const handleSuggestionClick = (suggestion) => {
    console.log("Search function was called in React!", suggestion);
    if (containsRestrictedKeyword(suggestion)) {
      toast.error(RESTRICTED_SEARCH_MESSAGE);
      setSuggestions([]);
      setLatestSuggestions([]);
      setDidYouMean(null);
      setIsDropdownOpen(false);
      onSuggestionsChange?.([]);
      return;
    }
    suppressSuggestionsRef.current = true;
    skipSuggestionFetchRef.current = true;
    requestIdRef.current += 1;
    setInput(suggestion);
    try {
      sessionStorage.setItem("shotkut-search-source", "typed");
    } catch (error) {
      // Ignore storage failures.
    }
    const params = new URLSearchParams();
    params.set("term", suggestion);
    preserveSearchModeInParams(params);
    navigate(`/search?${params.toString()}`);
    setIsDropdownOpen(false);
    setSuggestions([]);
    setLatestSuggestions([]);
    setDidYouMean(null);
    onSuggestionsChange?.([]);
    setActiveIndex(-1);
    setSearchState("SEARCHING");
    try {
      inputRef.current?.blur();
    } catch (e) { }
  };

  const handleSearch = (query) => {
    suppressSuggestionsRef.current = false;
    setInput(query);
    setActiveIndex(-1);
  };

  const handleFocus = () => {
    if (suppressSuggestionsRef.current) {
      return;
    }

    const trimmed = input.trim();
    if (trimmed.length >= MIN_CHARS && suggestions.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  useEffect(() => {
    const trimmed = input.trim();

    if (skipSuggestionFetchRef.current || suppressSuggestionsRef.current) {
      skipSuggestionFetchRef.current = false;
      setSuggestions([]);
      setLatestSuggestions([]);
      setDidYouMean(null);
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      setSearchState("IDLE");
      onSuggestionsChange?.([]);
      return;
    }

    if (!trimmed) {
      setSuggestions([]);
      setLatestSuggestions([]);
      setDidYouMean(null);
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      setSearchState("IDLE");
      onSuggestionsChange?.([]);
      return;
    }

    if (trimmed.length < MIN_CHARS) {
      setSuggestions([]);
      setLatestSuggestions([]);
      setDidYouMean(null);
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      setSearchState("TYPING_PARTIAL");
      onSuggestionsChange?.([]);
      return;
    }

    if (containsRestrictedKeyword(trimmed)) {
      requestIdRef.current += 1;
      setSuggestions([]);
      setLatestSuggestions([]);
      setDidYouMean(null);
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      setSearchState("IDLE");
      onSuggestionsChange?.([]);
      return;
    }

    // Instant local pin (fuzzy / exact) before the suggest API returns.
    applyPinnedSuggestions(trimmed, readPrefixSuggestionCache(trimmed));

    const requestId = ++requestIdRef.current;
    const handler = setTimeout(async () => {
      const latestInputMatches = () => {
        const latestInput = inputRef.current?.value?.trim() || "";
        return latestInput === trimmed && !suppressSuggestionsRef.current;
      };

      try {
        const params = new URLSearchParams();
        params.set("q", trimmed);
        params.set("limit", "5");

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/search/suggest?${params.toString()}`
        );

        if (requestIdRef.current !== requestId || !latestInputMatches()) {
          return;
        }

        const apiSuggestions = filterSafeSearchValues(
          response?.data?.suggestions || [],
        );
        writeSuggestionCache(trimmed, apiSuggestions);
        await applyPinnedSuggestions(trimmed, apiSuggestions);
      } catch (error) {
        if (requestIdRef.current !== requestId) {
          return;
        }
        // Keep local fuzzy pin even if suggest API fails.
        const pinned = await applyPinnedSuggestions(trimmed, []);
        if (!pinned.suggestions.length) {
          setSearchState("ERROR");
        }
      }
    }, SUGGESTION_DEBOUNCE_MS);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  return (
    <div
      id="navSearchBox"
      className={`navSearchDiv relative h-8 mt-4 right-0 mb-3 md:right-5 ${isPageVariant ? "search-input-page" : ""
        } ${isHeaderVariant ? "search-input-header" : ""} ${className}`}
      ref={dropdownRef}
    >
      <input
        ref={inputRef}
        className={`navSearch ${darkMode ? "placeholder-gray-400" : "placeholder-gray-600"} ${inputClassName}`}
        placeholder="What are you looking for?"
        value={input}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={handleFocus}
        onBlur={() => {
          window.setTimeout(() => {
            if (!dropdownRef.current?.contains(document.activeElement)) {
              setIsDropdownOpen(false);
            }
          }, 150);
        }}
        onKeyDown={handleKeyDown}
      />
      <img
        src={icon}
        onClick={handleSearchClick}
        className={`absolute right-3 ${isPageVariant ? "top-5" : "top-1/2"} -translate-y-1/2 size-6 cursor-pointer object-none active:scale-90 transition-transform duration-200`}
        alt="Search"
      />

      {isDropdownOpen && suggestions.length > 0 && (
        <div id="search-input-suggestions-dropdown" className="suggestions-dropdown search-suggestions-dropdown">
          {suggestions.map((suggestion, index) => {
            const isCorrectionRow = index === 0 && Boolean(didYouMean);
            return (
              <div
                key={`${suggestion}-${index}`}
                className={`suggestion-item relative${activeIndex === index ? " active" : ""
                  }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setActiveIndex(index)}
                aria-label={
                  isCorrectionRow
                    ? `Did you mean ${suggestion}?`
                    : undefined
                }
              >
                <img
                  src={icon}
                  className="absolute my-auto right-1 cursor-pointer top-1 bottom-1"
                  alt="Search Icon"
                />
                {isCorrectionRow ? `Did you mean: ${suggestion}` : suggestion}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchInput;
